import { ShieldCheck } from "lucide-react"
import Link from "next/link"

/**
 * First-party ownership disclosure for comparison content where one of the
 * sides is a WhatGrassIsThis property.
 *
 * This is NOT the FTC affiliate line. That one discloses that we earn a
 * commission when a reader buys someone else's product, and it renders further
 * down, under the buy chips it applies to. This one discloses that we wrote a
 * comparison in which we are a competitor, which a reader needs before they
 * read the table rather than after. Hence: above the fold, plain language, no
 * legalese, and never collapsed behind a disclosure widget.
 *
 * Styled with utilities rather than the `cmp-*` sheet so the roundup route can
 * use it without pulling in the whole compare stylesheet. It borrows the
 * translucent-tint-plus-blur construction of the surrounding glass surfaces but
 * keeps the amber hue, because a disclosure has to stay visually distinct from
 * the content it qualifies. Presentational and server-safe (no hooks, no
 * state), so it lands in the SSR HTML where crawlers and AI search read it.
 */
export function OwnershipDisclosure({
  text,
  className = "",
}: {
  text: string
  className?: string
}) {
  return (
    <aside
      aria-label="Ownership disclosure"
      className={`mx-auto flex max-w-[720px] items-start gap-2.5 rounded-2xl border border-amber-300/60 bg-amber-100/45 px-4 py-3 shadow-[0_12px_30px_-22px_rgba(120,80,10,0.5)] backdrop-blur-md ${className}`}
    >
      <span className="mt-0.5 grid h-[26px] w-[26px] flex-none place-items-center rounded-full border border-amber-200/70 bg-white/60 backdrop-blur-xs">
        <ShieldCheck className="h-[15px] w-[15px] text-amber-700" aria-hidden="true" />
      </span>
      <p className="m-0 text-sm leading-relaxed text-amber-900">
        {text}{" "}
        <Link
          href="/about"
          className="font-bold text-inherit underline underline-offset-2"
        >
          Who we are
        </Link>
        .
      </p>
    </aside>
  )
}
